import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, Menu } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { trpc } from "@/lib/trpc";

// ============================================================
// R1 — Identidade do RH e menu por processo (08/09)
// ============================================================
// O app deixou de ser "as escalas" e passou a ser o RH: o menu segue o
// processo do mes (painel, escala, conferencia, pagamento, comissao, cadastro,
// contrato) em vez de seguir a tabela do banco. As telas antigas que ainda
// funcionam nao sumiram — foram para o grupo "Mais", para nao quebrar o
// caminho de quem ja usa.
//
// O host nao aparece em lugar nenhum: escalas.vetcentermiguel.com e
// rh.vetcentermiguel.com servem exatamente este mesmo bundle.

type NavItem = { to: string; label: string; soAdmin?: boolean };

// Menu por processo, na ordem em que o mes acontece.
const navProcesso: NavItem[] = [
  { to: "/painel", label: "Painel" },
  { to: "/calendario", label: "Escalas" },
  { to: "/conferencia", label: "Conferência" },
  { to: "/pagamentos", label: "Pagamentos" },
  { to: "/comissoes", label: "Comissões" },
  { to: "/colaboradores", label: "Colaboradores" },
  { to: "/juridico", label: "Jurídico" },
];

// Telas que existiam antes do R1 e continuam valendo. Ficam agrupadas para o
// menu principal nao virar uma fileira de treze abas.
// "Meu extrato" aparece aqui também: quem é admin ou financeiro TAMBÉM é
// colaborador e tem os próprios turnos e o próprio recibo para ver. Antes o
// item só existia no menu de quem não é do financeiro, e a tela ficava sem
// porta para eles — a rota sempre existiu.
const navMais: NavItem[] = [
  { to: "/dashboard", label: "Início" },
  { to: "/meus-turnos", label: "Meus turnos" },
  { to: "/meu-extrato", label: "Meu extrato" },
  { to: "/remuneracao", label: "Remuneração" },
  // Só admin: /profissionais é cadastro de gente e a rota exige admin (App.tsx).
  { to: "/profissionais", label: "Profissionais", soAdmin: true },
  { to: "/solicitacoes", label: "Solicitações" },
];

const navAdmin: NavItem[] = [
  { to: "/admin", label: "Usuários e tipos de turno" },
  { to: "/admin/vinculos", label: "Vínculos com a clínica" },
];

export function Layout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const role = user?.role;
  const isAdmin = role === "admin";
  const isFinanceiro = role === "financeiro";
  // 24/09: o advogado. Menu com uma porta só: Jurídico.
  const isJuridico = role === "juridico";
  // 24/09: coordenação de escala (a Sthephany). Tem o menu de quem trabalha na
  // escala + a porta de tipos de turno e calendário (/admin, que para ela
  // mostra só essas duas abas). Nenhum item de RH ou dinheiro.
  const isEscala = role === "escala";
  // Quem so trabalha na escala (veterinario, auxiliar) enxerga a propria vida:
  // a escala e o proprio extrato. Dinheiro dos outros nao aparece.
  const somenteEscala = !isAdmin && !isFinanceiro && !isJuridico;
  // Quem está aguardando autorização aparece no menu do admin (24/09): o
  // pedido do advogado ficou dois dias sem ninguém ver.
  const usuariosQ = trpc.admin.listarUsuarios.useQuery(undefined, {
    enabled: isAdmin,
    staleTime: 60_000,
  });
  // Bloqueado (24/09) não é pedido de acesso: fica fora do contador.
  const aguardando = (usuariosQ.data ?? []).filter(
    (u) => u.role === "pendente" && !u.bloqueadoEm,
  ).length;

  const itens: NavItem[] = isJuridico
    ? [{ to: "/juridico", label: "Jurídico" }]
    : somenteEscala
    ? [
        { to: "/calendario", label: "Escalas" },
        ...(isEscala ? [{ to: "/admin", label: "Tipos de turno e calendário" }] : []),
        { to: "/meu-extrato", label: "Meu extrato" },
      ]
    : navProcesso;

  const extras: NavItem[] = (
    somenteEscala
      ? [
          { to: "/meus-turnos", label: "Meus turnos" },
          { to: "/solicitacoes", label: "Solicitações" },
        ]
      : navMais
  ).filter((item) => !isJuridico && (!item.soAdmin || isAdmin));

  async function logout() {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    navigate("/login");
    window.location.reload();
  }

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "rounded-md px-3 py-2 text-sm font-medium transition-colors",
      isActive
        ? "bg-brand-50 text-brand-700"
        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
    );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white no-print">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            {/* O logo continua caindo no /dashboard, que e a tela que ja
                funciona. O Painel novo entra no menu, mas ainda esta em
                construcao — nao serve de porta de entrada. */}
            <Link to="/dashboard" className="flex items-center gap-2 font-bold text-brand-700">
              <img
                src="/logo.png"
                alt="RH Vet Center Miguel"
                className="h-9 w-9 rounded-md object-cover"
              />
              <span className="text-base">RH Vet Center Miguel</span>
            </Link>
          </div>

          <nav className="hidden items-center gap-1 lg:flex">
            {itens.map((item) => (
              <NavLink key={item.to} to={item.to} className={linkClass}>
                {item.label}
              </NavLink>
            ))}

            {extras.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900">
                    Mais
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {extras.map((item) => (
                    <DropdownMenuItem key={item.to} asChild>
                      <Link to={item.to}>{item.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900">
                    Configurações
                    {aguardando > 0 && (
                      <span
                        className="rounded-full bg-amber-500 px-1.5 text-[11px] font-semibold text-white"
                        title={`${aguardando} aguardando autorização`}
                      >
                        {aguardando}
                      </span>
                    )}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {navAdmin.map((item) => (
                    <DropdownMenuItem key={item.to} asChild>
                      <Link to={item.to}>{item.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>

          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-600 lg:inline">{user?.name}</span>
            <Button variant="ghost" size="icon" onClick={logout} title="Sair">
              <LogOut className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setOpen((s) => !s)}
            >
              <Menu className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {open && (
          <div className="border-t border-slate-200 bg-white lg:hidden">
            <nav className="flex flex-col p-2">
              {[...itens, ...extras, ...(isAdmin ? navAdmin : [])].map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "rounded-md px-3 py-2 text-sm font-medium",
                      isActive ? "bg-brand-50 text-brand-700" : "text-slate-700 hover:bg-slate-100",
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
